import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, hasAdminPanelAccess } from '../contexts/AuthContext';
import Button from '../components/Button';
import { favoriteListingService } from 'api/services/FavoriteListingService';
import { listingViewModelToAdListing } from 'api/utils/listingMapper';
import type { AdListing } from 'types/adListing';
import '@fortawesome/fontawesome-free/css/all.min.css';

const Favoriler: React.FC = () => {
  const { logout, user } = useAuth();
  const [favorites, setFavorites] = useState<AdListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchFavorites = useCallback(async () => {
    if (!user?.id) {
      setFavorites([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setFetchError(null);
    try {
      const result = await favoriteListingService.getByUserId(user.id);
      if (result.isSuccess && result.data) {
        const list = result.data
          .filter((f) => f.listing != null)
          .map((f) =>
            listingViewModelToAdListing({ ...f.listing!, isUserFavorite: true })
          );
        setFavorites(list);
      } else {
        setFavorites([]);
        setFetchError(result.message ?? 'Favoriler yüklenemedi.');
      }
    } catch (err) {
      setFavorites([]);
      setFetchError(err instanceof Error ? err.message : 'Bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  const removeFromFavorites = async (listingId: string | number) => {
    if (!user?.id) return;
    if (!window.confirm('Bu ürünü favorilerden çıkarmak istediğinize emin misiniz?')) return;
    const result = await favoriteListingService.toggle({
      userId: user.id,
      listingId: String(listingId),
    });
    if (result.isSuccess) {
      setFavorites((prev) => prev.filter((item) => item.id !== listingId));
    }
  };

  return (
    <div className="min-vh-100 bg-light">
      {/* Header */}
      <header className="header sticky-top bg-white">
        <nav className="navbar navbar-expand-lg">
          <div className="container">
            {/* Logo */}
            <Link className="navbar-brand fw-bold text-primary" to="/">
              <i className="fas fa-laptop me-2"></i>SadeceBilgisayar.com
            </Link>

            {/* Arama Kutusu */}
            <div className="search-box mx-lg-4 flex-grow-1">
              <form className="d-flex">
                <div className="input-group">
                  <input type="search" 
                       className="form-control" 
                       placeholder="Dizüstü, masaüstü, parça ara..."
                       aria-label="Arama" />
                  <Button className="btn btn-primary" type="submit">
                    <i className="fas fa-search"></i>
                  </Button>
                </div>
              </form>
            </div>

            {/* Sağ Menü (Giriş Yapmış Kullanıcı) */}
            <div className="nav-right d-flex align-items-center">
              <Link to="/ilan-ver" className="btn btn-outline-primary me-3">
                <i className="fas fa-plus-circle me-2"></i>İlan Ver
              </Link>
              
              {/* Bildirimler */}
              <div className="dropdown me-3">
                <button
                  type="button"
                  className="btn btn-link text-primary position-relative border-0" 
                  style={{ textDecoration: 'none' }}
                  id="notificationsDropdown"
                  data-bs-toggle="dropdown" 
                  aria-expanded="false"
                >
                  <i className="fas fa-bell fs-5"></i>
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-white text-primary" style={{ border: '1px solid #0d6efd' }}>
                    3
                  </span>
                </button>
                <div className="dropdown-menu dropdown-menu-end" aria-labelledby="notificationsDropdown" style={{ borderTop: '3px solid #0d6efd', minWidth: '320px' }}>
                  <h6 className="dropdown-header bg-primary text-white">Bildirimler</h6>
                  <a className="dropdown-item d-flex align-items-center py-3" href="#">
                    <div className="notification-icon bg-primary text-white rounded-circle me-3 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                      <i className="fas fa-comment"></i>
                    </div>
                    <div>
                      <p className="mb-0 fw-semibold">İlanınıza yeni bir mesaj geldi</p>
                      <small className="text-muted">2 saat önce</small>
                    </div>
                  </a>
                  <a className="dropdown-item d-flex align-items-center py-3" href="#">
                    <div className="notification-icon bg-success text-white rounded-circle me-3 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                      <i className="fas fa-tag"></i>
                    </div>
                    <div>
                      <p className="mb-0 fw-semibold">Takip ettiğiniz ürünün fiyatı düştü</p>
                      <small className="text-muted">5 saat önce</small>
                    </div>
                  </a>
                  <a className="dropdown-item d-flex align-items-center py-3" href="#">
                    <div className="notification-icon bg-warning text-white rounded-circle me-3 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                      <i className="fas fa-money-bill-alt"></i>
                    </div>
                    <div>
                      <p className="mb-0 fw-semibold">Yeni bir teklif aldınız</p>
                      <small className="text-muted">1 gün önce</small>
                    </div>
                  </a>
                  <div className="dropdown-divider"></div>
                  <Link to="/notifications" className="dropdown-item text-center text-primary fw-semibold py-2">
                    Tüm Bildirimleri Gör
                  </Link>
                </div>
              </div>

              {/* Mesajlar */}
              <div className="dropdown me-3">
                <button
                  type="button"
                  className="btn btn-link text-primary position-relative border-0" 
                  style={{ textDecoration: 'none' }}
                  id="messagesDropdown"
                  data-bs-toggle="dropdown" 
                  aria-expanded="false"
                >
                  <i className="fas fa-envelope fs-5"></i>
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-white text-primary" style={{ border: '1px solid #0d6efd' }}>
                    2
                  </span>
                </button>
                <div className="dropdown-menu dropdown-menu-end" aria-labelledby="messagesDropdown" style={{ borderTop: '3px solid #0d6efd', minWidth: '320px' }}>
                  <h6 className="dropdown-header bg-primary text-white">Mesajlar</h6>
                  <a className="dropdown-item d-flex align-items-center py-3" href="#">
                    <div className="me-3">
                      <img src="/assets/images/202329115120__P2090010_.jpg" className="rounded-circle" width="40" height="40" alt="Kullanıcı" />
                    </div>
                    <div>
                      <p className="mb-0 fw-semibold">Mehmet Demir</p>
                      <small className="text-muted">Merhaba, ürün hala satılık mı?</small>
                    </div>
                    <small className="text-primary ms-auto">5dk</small>
                  </a>
                  <a className="dropdown-item d-flex align-items-center py-3" href="#">
                    <div className="me-3">
                      <img src="/assets/images/202329115120__P2090010_.jpg" className="rounded-circle" width="40" height="40" alt="Kullanıcı" />
                    </div>
                    <div>
                      <p className="mb-0 fw-semibold">Ayşe Yılmaz</p>
                      <small className="text-muted">Fiyatta pazarlık payı var mı?</small>
                    </div>
                    <small className="text-primary ms-auto">2s</small>
                  </a>
                  <div className="dropdown-divider"></div>
                  <Link to="/mesajlar" className="dropdown-item text-center text-primary fw-semibold py-2">
                    Tüm Mesajları Görüntüle
                  </Link>
                </div>
              </div>

              {/* Kullanıcı Menüsü */}
              <div className="dropdown">
                <button
                  type="button"
                  className="btn btn-link text-primary dropdown-toggle d-flex align-items-center border-0" 
                  style={{ textDecoration: 'none', fontWeight: 'normal' }}
                  id="userDropdown"
                  data-bs-toggle="dropdown" 
                  aria-expanded="false"
                >
                  <img src="/assets/images/202329115120__P2090010_.jpg" className="rounded-circle me-2" width="32" height="32" alt="Kullanıcı" />
                  <span style={{ fontWeight: 'normal' }}>{user?.name || 'Ahmet Yılmaz'}</span>
                </button>
                <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="userDropdown" style={{ borderTop: '3px solid #0d6efd', minWidth: '250px' }}>
                  <div className="px-3 py-3 border-bottom mb-2">
                    <div className="d-flex align-items-center">
                      <img src="/assets/images/202329115120__P2090010_.jpg" className="rounded-circle me-3" width="50" height="50" alt="Kullanıcı" />
                      <div>
                        <h6 className="mb-1 fw-bold">{user?.name || 'Ahmet Yılmaz'}</h6>
                        <p className="small text-muted mb-0">{user?.email || 'ahmet.yilmaz@example.com'}</p>
                      </div>
                    </div>
                  </div>
                  <li><Link className="dropdown-item py-2" to="/profil"><i className="fas fa-user me-2 text-primary"></i>Profilim</Link></li>
                  <li><Link className="dropdown-item py-2" to="/ilanlarim"><i className="fas fa-list me-2 text-primary"></i>İlanlarım</Link></li>
                  <li><Link className="dropdown-item py-2" to="/favoriler"><i className="fas fa-heart me-2 text-primary"></i>Favorilerim</Link></li>
                  {hasAdminPanelAccess(user) && (
                    <li><Link className="dropdown-item py-2" to="/admin-panel"><i className="fas fa-shield-alt me-2 text-primary"></i>Admin Panel</Link></li>
                  )}
                  <li className="border-top mt-2">
                    <button type="button" className="dropdown-item py-2 mt-2" onClick={logout}>
                      <i className="fas fa-sign-out-alt me-2 text-danger"></i>Çıkış Yap
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </nav>
      </header>

      {/* Ana İçerik */}
      <main className="container py-4">
        <h1 className="mb-4">Favori İlanlarım</h1>

        {fetchError && (
          <div className="alert alert-warning mb-3" role="alert">
            {fetchError}
          </div>
        )}

        {loading ? (
          <div className="text-center py-5 text-muted">
            <div className="spinner-border text-primary mb-2" role="status" />
            <p>Favoriler yükleniyor...</p>
          </div>
        ) : (
          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
            {favorites.length > 0 ? (
              favorites.map((item) => (
                <div className="col" key={item.id}>
                  <div className="card h-100 product-card position-relative">
                    <Button
                      type="button"
                      className="btn position-absolute end-0 top-0 m-2 p-1 bg-white rounded-circle shadow-sm favorite-btn"
                      onClick={() => removeFromFavorites(item.id)}
                    >
                      <i className="fas fa-heart-broken text-danger" style={{ fontSize: '16px' }} />
                    </Button>

                    <Link to={`/ilan-detay-login/${item.id}`}>
                      <img
                        src={item.resimUrl || '/assets/images/placeholder.jpg'}
                        className="card-img-top"
                        alt={item.baslik}
                        style={{ height: '200px', objectFit: 'cover' }}
                      />
                    </Link>

                    <div className="card-body">
                      <Link to={`/ilan-detay-login/${item.id}`} className="text-decoration-none">
                        <h5 className="card-title text-dark">{item.baslik}</h5>
                      </Link>

                      <div className="d-flex justify-content-between mt-auto">
                        <small className="text-muted">
                          <i className="fas fa-map-marker-alt me-1" />
                          {item.konum}
                        </small>
                        <span className="h5 mb-0 text-primary fw-bold">
                          {item.fiyat.toLocaleString('tr-TR')} ₺
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-5 w-100">
                <i className="fas fa-heart fa-3x text-muted mb-3" />
                <h3>Henüz favori ilanınız yok</h3>
                <p className="text-muted">
                  Beğendiğiniz ilanları favorilere ekleyerek daha sonra kolayca ulaşabilirsiniz.
                </p>
                <Link className="btn btn-primary" to="/">
                  İlanlara Göz At
                </Link>
              </div>
            )}
          </div>
        )}
      </main>

      {/* CSS ekleyelim */}
      <style>
        {`
        .product-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        
        .product-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
        }
        
        .specs-container {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .spec-item {
          display: flex;
          align-items: center;
          font-size: 0.9rem;
        }
        
        .spec-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background-color: #f8f9fa;
        }
        
        .favorite-btn {
          z-index: 10;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }
        
        .favorite-btn:hover {
          transform: scale(1.1);
        }
        
        /* Dropdown Styles */
        .dropdown-menu {
            box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
            animation: fadeIn 0.3s ease;
        }

        .dropdown-header {
            font-weight: 600;
            letter-spacing: 0.5px;
            border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        }

        .dropdown-item {
            padding: 0.6rem 1.2rem;
            transition: all 0.2s;
        }

        .dropdown-item:hover {
            background-color: #f0f7ff;
            padding-left: 1.5rem;
        }

        .dropdown-item:active {
            background-color: #0d6efd;
            color: white;
        }

        .dropdown-item:active i {
            color: white !important;
        }

        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
        }

        /* Navbar Styles */
        .btn-link {
            text-decoration: none !important;
            box-shadow: none !important;
            border: none !important;
            outline: none !important;
        }

        .btn-link:focus, .btn-link:hover, .btn-link:active {
            text-decoration: none !important;
            box-shadow: none !important;
            border: none !important;
            outline: none !important;
        }

        .dropdown-toggle::after {
            vertical-align: middle;
            margin-left: 0.4em;
        }
        `}
      </style>

      {/* Footer */}
      <footer className="footer mt-auto py-5 bg-white border-top">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-3 text-center">
              <h5 className="mb-3 text-dark">Hakkımızda</h5>
              <p className="text-secondary">
                SadeceBilgisayar.com, sıfır veya İkinci el teknolojik ürünleri güvenilir bir şekilde alım veya satım yapabileceğiniz bir platformdur.
              </p>
            </div>
            <div className="col-lg-3 text-center">
              <h5 className="mb-3 text-dark">Önemli Tavsiyeler</h5>
              <ul className="list-unstyled">
                <li className="mb-2">
                  <Link className="text-secondary text-decoration-none d-inline text-nowrap" to="/masaustu_tavsiyeler">
                    Masaüstü alırken dikkat edilmesi gerekenler
                  </Link>
                </li>
                <li className="mb-2">
                  <Link className="text-secondary text-decoration-none d-inline" to="/laptop-tavsiyeler">
                    Laptop alırken dikkat edilmesi gerekenler
                  </Link>
                </li>
                <li className="mb-2">
                  <Link className="text-secondary text-decoration-none d-inline" to="/donanim-tavsiyeler">
                    Donanım alırken dikkat edilmesi gerekenler
                  </Link>
                </li>
              </ul>
            </div>
            <div className="col-lg-3 text-center">
              <h5 className="mb-3 text-dark">İletişim</h5>
              <div className="contact-info">
                <p className="text-secondary mb-2">Görüşleriniz ve şikayetleriniz için</p>
                <p className="text-secondary mb-2">
                  <i className="fas fa-envelope me-2"></i>
                  info@sadecebilgisayar.com
                </p>
              </div>
            </div>
            <div className="col-lg-3 text-center">
              <h5 className="mb-3 text-dark">Sosyal Medya Hesaplarımız</h5>
              <div className="social-links">
                <a className="text-primary me-4" href="#">
                  <i className="fab fa-facebook fa-3x"></i>
                </a>
                <a className="text-danger me-4" href="#">
                  <i className="fab fa-instagram fa-3x"></i>
                </a>
                <a className="text-danger" href="#">
                  <i className="fab fa-youtube fa-3x"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="footer-bottom mt-5 pt-4 border-top">
            <div className="row">
              <div className="col-md-6">
                <p className="text-secondary mb-0">© 2024 SadeceBilgisayar.com - Tüm hakları saklıdır.</p>
              </div>
              <div className="col-md-6 text-md-end">
                <Link className="text-secondary me-3" to="/gizlilik-sozlesmesi">Gizlilik Politikası</Link>
                <Link className="text-secondary me-3" to="/kullanici-sozlesmesi">Kullanım Koşulları</Link>
                <Link className="text-secondary" to="/cerez-politikasi">Çerez Politikası</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Favoriler;
